import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import DataFactoryCalc from '../components/calculators/DataFactoryCalc';
import { CalculatorContentWrapper } from '../components/shared/CalculatorContentWrapper';
import { generateBreadcrumbList } from '../utils/schemaHelpers';

export default function DataFactoryPage() {
  return (
    <>
      <Helmet>
        <title>Azure Data Factory Cost Calculator UK | AzureCalc.uk</title>
        <meta
          name="description"
          content="Estimate Azure Data Factory costs in GBP for UK South. Calculate DIU-hours for Copy Activity, Data Flow vCore costs, Self-hosted IR charges, and pipeline orchestration. Updated nightly."
        />
        <link rel="canonical" href="https://www.azure-calc.co.uk/data-factory" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: 'How to estimate Azure Data Factory costs for UK South',
          description: 'Use the AzureCalc.uk calculator to estimate your Azure Data Factory monthly costs in GBP.',
          step: [
            { '@type': 'HowToStep', position: 1, name: 'Choose your workload type', text: 'Select between cloud pipelines, data flows, SHIR (Self-Hosted IR), or data transformation roles.' },
            { '@type': 'HowToStep', position: 2, name: 'Enter runtime hours', text: 'Input your monthly usage in terms of DIU-hours or vCore hours, depending on your activity type.' },
            { '@type': 'HowToStep', position: 3, name: 'Estimate optimization costs', text: 'The calculator shows baseline costs and highlights where optimization (SHIR, batch scheduling) can reduce spend.' },
            { '@type': 'HowToStep', position: 4, name: 'Review the GBP breakdown', text: 'See the line-by-line cost in UK South pounds, including transformations, IR licensing, and data movement.' },
          ],
        })}</script>
        <script type="application/ld+json">{JSON.stringify(generateBreadcrumbList([{ name: 'Home', url: '/', position: 1 }, { name: 'Azure Data Factory Calculator', url: '/data-factory', position: 2 }]))}</script>
      </Helmet>
      <div className="p-6 md:p-8">
        <div className="mb-6">
          <h1 className="text-3xl font-extrabold tracking-tighter text-on-surface">
            Azure Data Factory Cost Calculator
          </h1>
          <p className="text-on-surface-variant font-medium mt-1 leading-relaxed">
            UK South pricing · Copy Activity (DIU-hours) · Data Flow · Self-hosted IR · Pipeline orchestration
          </p>
        </div>
        <DataFactoryCalc />

        <CalculatorContentWrapper
          eyebrow="Pricing Guide"
          title="Data Factory billing is driven by activity complexity, not just throughput"
          intro={
            <>
              <p className="mb-3">
                Azure Data Factory is deceptively priced because the cost depends on _what_ you're doing, not just
                how often. A simple copy of 1 GB from Blob Storage to SQL Database might cost £0.005, but an equivalent
                copy routed through a Data Flow (transformation) can cost £0.50+ because it spins up compute. Teams often
                miscalculate because they focus on ingestion volume and ignore pipeline orchestration, data flow vCore
                hours, and self-hosted integration runtime fees. The result: invoices that look reasonable but carry hidden
                architecture costs baked into the transformation logic.
              </p>
              <p>
                This page keeps the calculator as the primary action, then explains the cost drivers and optimization
                paths that distinguish a cheap extraction workflow from an expensive replatforming pipeline.
              </p>
            </>
          }
          sections={[
            {
              id: 'activity-types',
              title: 'Cost drivers: Copy, Data Flow, and Orchestration',
              body: (
                <>
                  <p className="mb-3">
                    Data Factory bills for three primary activity types:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 mb-3">
                    <li><strong>Copy Activity:</strong> Moves data without transformation. Cost scales with DIU-hours
                      (Data Integration Units × hours consumed). DIU are compute slots assigned to the copy task; more
                      DIUs = higher parallelism but higher cost. A simple copy to a managed connector (e.g., SQL Database,
                      Blob Storage) is cheaper than copying to self-managed sources or using custom logic.</li>
                    <li><strong>Data Flow (Mapping Data Flow):</strong> Performs transformations (filtering, joining,
                      aggregating, writing). Spins up Apache Spark clusters per vCore-hour. Data flows are the heaviest cost
                      component and are often overlooked during design because engineers focus on logical correctness, not
                      cost per GB-to-process.</li>
                    <li><strong>Orchestration:</strong> Each pipeline run and activity execution are separately billable.
                      A pipeline that triggers 1,000 times per month with 5 activities = thousands of billable orchestration
                      events. This is usually cheap (£0.0001–0.001 per activity run) but accumulates.</li>
                  </ul>
                  <p>
                    Self-hosted integration runtimes add another layer: a standing monthly charge plus per-execution
                    overhead. Using a self-hosted IR for a heavy workload can triple the cost compared to managed runtime.
                  </p>
                </>
              ),
            },
            {
              id: 'diu-hours-deep-dive',
              title: 'DIU-hours: where Copy Activity costs accumulate',
              body: (
                <>
                  <p className="mb-3">
                    A DIU (Data Integration Unit) is a measure of compute that Data Factory assigns to a Copy Activity.
                    You select a "number of DIUs" (1, 2, 4, up to 256 depending on the runtime), and Data Factory consumes
                    DIU-hours at a rate of ~£0.0069 per DIU-hour in UK South. The formula is simple:
                  </p>
                  <p className="mb-3 p-3 rounded" style={{ backgroundColor: 'var(--surface-container)' }}>
                    <strong>Cost = DIUs × Hours × £0.0069/DIU-hour</strong>
                  </p>
                  <p className="mb-3">
                    The trap is that teams often don't optimize the DIU count. Assigning 4 DIUs to copy 100 MB (which could
                    run on 1 DIU in 5 seconds) wastes cluster compute. Conversely, assigning 1 DIU to copy 10 GB from a
                    slow on-premises source may take 30 minutes instead of 5, consuming far more total DIU-hours than
                    parallelizing with more DIUs would.
                  </p>
                  <p>
                    The per-GB cost depends on network topology, source/sink type, and complexity. A cloud-to-cloud copy
                    (e.g., Blob to SQL Database) is cheap; a copy from a legacy on-premises ERP via a self-hosted IR while
                    applying transformations is much more expensive.
                  </p>
                </>
              ),
            },
            {
              id: 'data-flow-cost-shock',
              title: 'Data Flow (most expensive): vCore-hours for transformations',
              body: (
                <>
                  <p className="mb-3">
                    A Mapping Data Flow executes transformations using Spark clusters. Unlike Copy Activity (which is dumb,
                    just moves bytes), Data Flows must parse, transform, and serialize, which requires compute. Cost is:
                  </p>
                  <p className="mb-3 p-3 rounded" style={{ backgroundColor: 'var(--surface-container)' }}>
                    <strong>Cost = Compute vCores × Runtime hours × £0.26–0.40/vCore-hour</strong>
                  </p>
                  <p className="mb-3">
                    A 4-core Data Flow running for 10 minutes = 4 × 0.1667 hours × £0.30 ≈ £0.20 per run. Multiply by
                    daily triggers, and a "simple" aggregation can cost £6/month. Scale to a weekly full rebuild of a data
                    warehouse with join-heavy logic, and a single Data Flow run can cost £5–20+.
                  </p>
                  <p>
                    The counterintuitive optimization: sometimes it's cheaper to export raw data from Data Factory to
                    Synapse, Databricks, or SQL and run transformations there, where you can cache, re-use compute,
                    or pausa between jobs. A naive "put all the logic in Data Flow" architecture can easily cost 2–3× as much
                    as a deliberate multi-service design.
                  </p>
                </>
              ),
            },
            {
              id: 'self-hosted-ir',
              title: 'On-premises coupling: self-hosted integration runtime costs',
              body: (
                <>
                  <p className="mb-3">
                    A self-hosted integration runtime (IR) runs in your environment (on-premises or on-VM) and connects
                    Azure Data Factory to local data sources. Cost includes:
                  </p>
                  <ul className="list-disc pl-5 space-y-1 mb-3">
                    <li>Infrastructure: You provide or pay for the VM/hardware yourself (not ADF's charge, but your cost).</li>
                    <li>ADF licensing: ~£0.80 per node per hour. A 2-node cluster costs ~£16/month (assuming 24/7 running).</li>
                    <li>Activity execution: Per-activity cost applied on top of the IR licensing.</li>
                  </ul>
                  <p>
                    Teams often underbudget this because the IR licensing is conflated with infrastructure costs. A persistent
                    2-node self-hosted IR costs £500–600/month _before_ DIU or Data Flow costs are added.
                  </p>
                  <p>
                    <strong>Optimization:</strong> Limit self-hosted IRs to on-premises sources that absolutely cannot use managed
                    connectors. Consider replicating data to Azure first, then processing, rather than pulling through an IR gate.
                  </p>
                </>
              ),
            },
            {
              id: 'worked-example',
              title: 'Worked example: migrating data warehouse with hidden Data Factory costs',
              body: (
                <>
                  <p className="mb-3">
                    A company migrates a 100 GB data warehouse from on-premises to Azure quarterly:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 mb-3">
                    <li>1. Extract raw data via self-hosted IR to Blob Storage (Copy Activity, 4 DIUs, 2 hours)</li>
                    <li>2. Transform with Mapping Data Flow (join, aggregate, deduplicate, 4 vCores, 1 hour)</li>
                    <li>3. Load to SQL Synapse (Copy Activity, 2 DIUs, 30 min)</li>
                    <li>4. Orchestration: 1 pipeline run + 3 activities = 3 orchestration events</li>
                  </ul>
                  <p className="mb-2">
                    Cost per migration run (estimated):
                  </p>
                  <ul className="list-disc pl-5 space-y-1 mb-3">
                    <li>Self-hosted IR licensing: Sunk cost (£500–600/month for the node itself; let's exclude)</li>
                    <li>Copy Activity (DIU): 4 DIUs × 2.5 hours × £0.0069 = £0.07 + activity execution overhead</li>
                    <li>Data Flow (vCore-hours): 4 vCores × 1 hour × £0.35 = £1.40 + activity execution overhead</li>
                    <li>Orchestration: 3 events × £0.0001 ≈ £0.0003</li>
                    <li><strong>Per-run total:</strong> ~£1.50–2.00</li>
                  </ul>
                  <p>
                    One quarterly run = £2. But in reality, teams do weekly test migrations, monthly incremental loads, and
                    ad-hoc re-runs to verify logic. End result: £8–15/month for a process that "should" cost £0.50 because
                    the engineering team didn't optimize DIU counts or question whether Data Flow was the right tool.
                  </p>
                </>
              ),
            },
            {
              id: 'optimization-paths',
              title: 'Cost optimization strategies',
              body: (
                <>
                  <p className="mb-3">
                    <strong>Right-size DIU and vCore allocation.</strong> Start small (1–2 DIUs), measure runtime,
                    then scale up only if bottlenecked by network/serialization, not raw throughput.
                  </p>
                  <p className="mb-3">
                    <strong>Prefer Copy Activity over Data Flow for simple moves.</strong> If you're not transforming
                    (just filtering or moving), a Copy Activity is 10–20× cheaper.
                  </p>
                  <p className="mb-3">
                    <strong>Push logic to the target (e.g., SQL, Synapse, Databricks).</strong> Receive raw data with a cheap
                    Copy Activity, then transform in-place. This avoids Data Flow licensing and lets you batch, cache, and
                    optimize at scale.
                  </p>
                  <p className="mb-3">
                    <strong>Minimize self-hosted IR usage.</strong> Only use for connectivity that cannot reach Azure from managed runtimes.
                    If you migrate on-prem data, load it to Azure first, then process from there.
                  </p>
                  <p>
                    <strong>Schedule triggers carefully.</strong> Each pipeline execution is billable. Batch multiple jobs into
                    one run instead of triggering atomically every few minutes.
                  </p>
                </>
              ),
            },
            {
              id: 'faq',
              title: 'Common questions',
              body: (
                <>
                  <p className="mb-3">
                    <strong>Is there a free tier for Data Factory?</strong> Data Factory has no free tier, but the orchestration
                    and activity execution charges are small. The bulk of the cost comes from DIU-hours (Copy) and vCore-hours
                    (Data Flow).
                  </p>
                  <p className="mb-3">
                    <strong>Can I reduce costs by switching to Azure Synapse pipelines?</strong> Synapse has similar activity
                    costs but integrates native SQL and Spark compute differently. If you're already running Synapse, embedding
                    pipelines can be cheaper than separate Data Factory orchestration, but the Decision depends on workload.
                  </p>
                  <p className="mb-3">
                    <strong>Is Data Flow cheaper in Azure Databricks?</strong> Databricks has its own pricing but offers more
                    flexibility for optimization and cost-sharing with other tenants. Direct substitution is not straightforward;
                    cost depends on cluster size and utilization.
                  </p>
                  <p>
                    <strong>What if I use a scheduled Copy Activity vs a Mapping Data Flow for the same task?</strong> Copy
                    Activity is almost always cheaper for data movement alone. Use Data Flow only when you need transformations
                    that SQL in the target cannot handle efficiently.
                  </p>
                </>
              ),
            },
          ]}
          methodologyNote={
            <>
              <p className="mb-2">
                Rates are sourced from the Azure Retail Prices API for UK South in GBP. The calculator includes DIU-hours
                for Copy Activity, vCore-hours for Data Flow, self-hosted IR licensing, and orchestration overhead.
                Actual costs depend on data complexity, transformation logic, and integration runtime topology.
              </p>
              <Link to="/methodology" className="text-[#0078d4] hover:underline">
                How prices are calculated →
              </Link>
            </>
          }
          relatedLinks={[
            { label: 'Blob Storage Calculator', to: '/blob-storage' },
            { label: 'Log Analytics Calculator', to: '/log-analytics' },
            { label: 'Home', to: '/' },
          ]}
        />
      </div>
    </>
  );
}
